import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";

const ROLES = [
  { v: "professional", l: "Professional / Employee / Freelancer" },
  { v: "organization", l: "Organization / HR / Recruiter" },
  { v: "institution", l: "Institution / Faculty" },
  { v: "verifier", l: "Verifier (Employer Verifier / Background Checks)" },
];

// Role-specific third consent (the per-role agreement)
const ROLE_AGREEMENT = {
  professional: { slug: "individual-consent", label: "Individual Professional Passport Consent Agreement" },
  organization: { slug: "employer-agreement", label: "Employer Authorization & Issuer Agreement" },
  institution: { slug: "institution-agreement", label: "Institution Authorization & Issuer Agreement" },
  verifier: { slug: "recruiter-agreement", label: "Recruiter Verification Usage Agreement" },
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const presetRole = params.get("role");
  const validRoles = ["professional", "organization", "institution", "verifier"];
  const initialRole = validRoles.includes(presetRole) ? presetRole : "professional";
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    role: initialRole,
    organization_name: "",
    phone: "",
  });
  const [consents, setConsents] = useState({ terms: true, privacy: true, role: true, marketing: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mockOtp, setMockOtp] = useState("");

  // If query param changes after mount (e.g., user clicks a different CTA), update.
  useEffect(() => {
    if (presetRole && validRoles.includes(presetRole) && presetRole !== form.role) {
      setForm((p) => ({ ...p, role: presetRole }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetRole]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const agreement = ROLE_AGREEMENT[form.role];
  const consentsValid = consents.terms && consents.privacy && consents.role;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consentsValid) {
      setError("Please accept the required agreements to continue.");
      return;
    }
    setLoading(true);
    setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Passport created — welcome to EthicCred");
      if (res.mockOtp) setMockOtp(res.mockOtp);
      setTimeout(() => nav("/dashboard"), 1200);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ec-bg" data-testid="register-page">
      {/* Left: Form */}
      <div className="flex flex-col px-6 sm:px-10 py-10 lg:p-16">
        <Link to="/" className="w-fit">
          <Logo size={34} />
        </Link>

        <div className="flex-1 flex items-center mt-10">
          <div className="w-full max-w-lg">
            <div className="eyebrow">Claim your Passport</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Become globally verifiable.
            </h1>
            <p className="text-ec-ink-soft mt-3">
              Get your unique EthicCred ID, a digital Professional Passport, and your Credibility
              Score — free, forever.
            </p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="register-form">
              <div>
                <Label className="eyebrow">I am a…</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-sm border-ec-line" data-testid="register-role-trigger">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.v} value={r.v} data-testid={`register-role-option-${r.v}`}>
                        {r.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name" className="eyebrow">Full name</Label>
                  <Input
                    id="full_name"
                    required
                    value={form.full_name}
                    onChange={set("full_name")}
                    className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="eyebrow">Phone (optional)</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={set("phone")}
                    className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              {(form.role === "organization" || form.role === "institution") && (
                <div>
                  <Label htmlFor="organization_name" className="eyebrow">
                    {form.role === "institution" ? "Institution name" : "Organization name"}
                  </Label>
                  <Input
                    id="organization_name"
                    required
                    value={form.organization_name}
                    onChange={set("organization_name")}
                    className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                    data-testid="register-org-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="eyebrow">
                  Email {(form.role === "organization" || form.role === "institution" || form.role === "verifier") && (
                    <span className="ml-1 text-ec-emerald font-mono text-[10px]">· business domain required</span>
                  )}
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={
                    form.role === "professional"
                      ? "you@gmail.com"
                      : form.role === "institution"
                      ? "admin@institution.edu"
                      : "hr@company.com"
                  }
                  className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                  data-testid="register-email-input"
                />
                {(form.role === "organization" || form.role === "institution" || form.role === "verifier") && (
                  <p className="text-[11px] text-ec-ink-soft mt-1.5 font-mono-plex">
                    Personal email providers (gmail, yahoo, outlook, hotmail, icloud, etc.) are not accepted for {form.role} accounts.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="password" className="eyebrow">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                  data-testid="register-password-input"
                />
              </div>

              {error && (
                <div className="text-sm text-ec-error border border-ec-error/30 bg-ec-error/5 px-3 py-2 rounded-sm" data-testid="register-error">
                  {error}
                </div>
              )}
              {mockOtp && (
                <div className="text-xs font-mono-plex border border-ec-emerald/30 bg-ec-emerald/10 text-ec-emerald px-3 py-2 rounded-sm flex items-start gap-2" data-testid="register-mock-otp">
                  <CheckCircle2 className="w-4 h-4 mt-0.5" /> {mockOtp}
                </div>
              )}

              <div className="border-t border-ec-line pt-5 space-y-3" data-testid="register-consents">
                <ConsentRow
                  required
                  checked={consents.terms}
                  onChange={(v) => setConsents({ ...consents, terms: v })}
                  testid="consent-terms"
                >
                  I agree to the{" "}
                  <Link to="/legal/terms-of-service" target="_blank" className="font-semibold text-ec-ink underline underline-offset-2 hover:text-ec-emerald">
                    Terms of Service
                  </Link>
                </ConsentRow>
                <ConsentRow
                  required
                  checked={consents.privacy}
                  onChange={(v) => setConsents({ ...consents, privacy: v })}
                  testid="consent-privacy"
                >
                  I agree to the{" "}
                  <Link to="/legal/privacy-policy" target="_blank" className="font-semibold text-ec-ink underline underline-offset-2 hover:text-ec-emerald">
                    Privacy Policy
                  </Link>
                </ConsentRow>
                <ConsentRow
                  required
                  checked={consents.role}
                  onChange={(v) => setConsents({ ...consents, role: v })}
                  testid="consent-role"
                >
                  I agree to the{" "}
                  <Link to={`/legal/${agreement.slug}`} target="_blank" className="font-semibold text-ec-ink underline underline-offset-2 hover:text-ec-emerald">
                    {agreement.label}
                  </Link>
                </ConsentRow>
                <ConsentRow
                  checked={consents.marketing}
                  onChange={(v) => setConsents({ ...consents, marketing: v })}
                  testid="consent-marketing"
                >
                  I would like to receive updates and promotional communications from EthicCred (optional)
                </ConsentRow>
              </div>

              <Button
                type="submit"
                disabled={loading || !consentsValid}
                className="w-full h-11 rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2 disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? "Issuing passport…" : "Get Started"} <ArrowUpRight className="w-4 h-4" />
              </Button>
            </form>

            <p className="mt-8 text-sm text-ec-ink-soft">
              Already verified?{" "}
              <Link to="/login" className="font-semibold text-ec-ink underline underline-offset-4 hover:text-ec-emerald" data-testid="register-to-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex bg-ec-soft p-16 relative overflow-hidden border-l border-ec-line">
        <div className="self-center max-w-md">
          <div className="stamp text-ec-emerald w-fit">Verified</div>
          <h3 className="font-display text-4xl font-black tracking-tight mt-6 leading-tight">
            Your career, signed by the people who lived it with you.
          </h3>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              "Unique global EthicCred ID",
              "0–1000 Credibility Score",
              "QR + verification URL on every credential",
              "Tamper-proof, lifetime portable record",
            ].map((it) => (
              <li key={it} className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-ec-emerald mt-0.5" />
                {it}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ checked, onChange, children, required, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testid}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" data-testid={`${testid}-checkbox`} />
      <span className="text-sm text-ec-ink-soft leading-relaxed">
        {required && <span className="text-ec-emerald font-bold mr-0.5">*</span>}
        {children}
      </span>
    </label>
  );
}
